import { type FocusEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
    type TDealDetails,
    useUpdateDealDetailsMutation,
} from '@crm/entities/deal';
import EditSvg from '@crm/shared/assets/icons/edit.svg?react';
import { cn } from '@crm/shared/lib';
import { Button, InputError, SvgIcon } from '@crm/shared/ui';

import { DealTitle } from './DealTitle';
import { handleTitleUpdateError } from '../../model/handle-title-update-error';
import { DealStatus } from '../DealStatus';

interface HeaderProps {
    dealDetailsData: TDealDetails;
}

export const Header = ({ dealDetailsData }: HeaderProps) => {
    const { t } = useTranslation();
    const { dealData } = dealDetailsData;
    const updateDealDetailsMutation = useUpdateDealDetailsMutation();
    const titleInputRef = useRef<HTMLInputElement>(null);
    const isSavingRef = useRef(false);
    const editVersionRef = useRef(0);
    const [isTitleEditing, setIsTitleEditing] = useState(false);
    const [titleValue, setTitleValue] = useState('');
    const [titleError, setTitleError] = useState<string | undefined>();

    useEffect(() => {
        if (isTitleEditing) {
            titleInputRef.current?.focus();
            titleInputRef.current?.select();
        }
    }, [isTitleEditing]);

    const updateTitle = async (nextValue: string) => {
        if (isSavingRef.current) {
            return;
        }

        const submitValue = nextValue.trim();
        const currentValue = String(dealData?.name ?? '');

        if (!submitValue) {
            setTitleError(t('crm.dealDetailsPage.header.validation.titleRequired'));
            return;
        }

        setTitleError(undefined);

        if (!dealData?.id || submitValue === currentValue) {
            setIsTitleEditing(false);
            return;
        }

        isSavingRef.current = true;
        const editVersion = editVersionRef.current;

        try {
            await updateDealDetailsMutation.mutateAsync({
                dealId: dealData.id,
                payload: {
                    name: submitValue,
                },
            });
            if (editVersionRef.current === editVersion) {
                setIsTitleEditing(false);
            }
        } catch (error) {
            if (editVersionRef.current === editVersion) {
                setTitleError(handleTitleUpdateError(error, t('crm.common.deal.errors.update')));
            }
        } finally {
            isSavingRef.current = false;
        }
    };

    const handleTitleBlur = (event: FocusEvent<HTMLInputElement>) => {
        if (titleError) {
            return;
        }

        void updateTitle(event.currentTarget.value);
    };

    const handleTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void updateTitle(event.currentTarget.value);
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            editVersionRef.current += 1;
            setTitleValue(dealData?.name ?? '');
            setTitleError(undefined);
            setIsTitleEditing(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
                <div className="flex min-h-7 min-w-0 items-center">
                    {isTitleEditing ? (
                        <div className="group/input-group relative w-72 min-w-0">
                            <input
                                ref={titleInputRef}
                                value={titleValue}
                                aria-label={t('crm.common.deal.title')}
                                aria-invalid={Boolean(titleError)}
                                aria-describedby={titleError ? 'deal-title-error' : undefined}
                                className={cn(
                                    '-my-1.5 h-7 w-full min-w-0 rounded-lg border border-transparent bg-transparent px-2 py-1 text-xs leading-4 font-normal text-smtr-main-black-100 transition-colors outline-none placeholder:text-smtr-main-black-100 hover:border-gray-200 hover:bg-white focus:border-gray-200 focus:bg-white focus:shadow-sm aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
                                    titleError && 'pr-7'
                                )}
                                onBlur={handleTitleBlur}
                                onChange={(event) => {
                                    editVersionRef.current += 1;
                                    setTitleValue(event.currentTarget.value);
                                    setTitleError(undefined);
                                }}
                                onKeyDown={handleTitleKeyDown}
                                // maxLength={DEAL_NAME_MAX_LENGTH}
                                maxLength={257}

                            />
                            {titleError && (
                                <InputError
                                    tooltipId="deal-title-error"
                                    errorMessage={titleError}
                                    className="absolute top-1/2 right-1.5 z-50 -translate-y-1/2 p-0"
                                    iconClassName="size-3.5"
                                    tooltipSide="bottom"
                                    tooltipClassName="max-w-52"
                                />
                            )}
                        </div>
                    ) : (
                        <DealTitle key={dealData?.name} name={dealData?.name ?? ''} />
                    )}
                </div>
                <Button
                    className="size-4 text-smtr-main-tertiary-100 hover:bg-transparent hover:text-smtr-main-black-100"
                    variant="ghost"
                    type="button"
                    onMouseDown={(event) => {
                        if (isTitleEditing) {
                            event.preventDefault();
                        }
                    }}
                    onClick={() => {
                        if (isTitleEditing) {
                            void updateTitle(titleValue);

                            return;
                        }

                        setTitleValue(dealData?.name ?? '');
                        setTitleError(undefined);
                        setIsTitleEditing(true);
                    }}
                >
                    <SvgIcon icon={EditSvg} size="16" color="currentColor" />
                </Button>
            </div>
            <DealStatus dealDetailsData={dealDetailsData} />
        </div>
    );
};
